import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, adminSetPendingStoreOwner, adminClearStoreOwner } from '../supabaseClient';
import StoreRequestForm from './StoreRequestForm';
import { getStoreHost, getStoreKioskUrl } from '../storeDomains';
import './Dashboard.css';

const getStoreData = (restaurantId) => {
  try {
    return JSON.parse(localStorage.getItem(`tenant:${restaurantId}:ss_store_v1`) || '{}');
  } catch {
    return {};
  }
};

const getStoreLogo = (restaurant) => {
  const local = getStoreData(restaurant.id).logo;
  return local || restaurant.store_settings?.logo || null;
};

const getStoreIndustry = (restaurant) => {
  const local = getStoreData(restaurant.id).industry;
  return local || restaurant.store_settings?.industry || null;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const AVATAR_COLORS = [
  ['#2563eb','#1d4ed8'], ['#7c3aed','#6d28d9'], ['#059669','#047857'],
  ['#dc2626','#b91c1c'], ['#d97706','#b45309'], ['#0891b2','#0e7490'],
];

const getAvatarColor = (id) => {
  if (!id) return AVATAR_COLORS[0];
  const i = parseInt(id.replace(/-/g,'').slice(0,4), 16) % AVATAR_COLORS.length;
  return AVATAR_COLORS[i];
};

const getShortId = (uuid) => {
  if (!uuid) return '0000';
  const hex = uuid.replace(/-/g, '').slice(-8);
  return String(parseInt(hex, 16) % 9000 + 1000);
};

const ORDER_KEY = (userId) => `ss_store_order_${userId}`;

const applyOrder = (list, orderedIds) => {
  if (!orderedIds?.length) return list;
  const map = Object.fromEntries(list.map(r => [r.id, r]));
  const ordered = orderedIds.filter(id => map[id]).map(id => map[id]);
  const rest = list.filter(r => !orderedIds.includes(r.id));
  return [...ordered, ...rest];
};

const getOwnerEmailSuggestions = (restaurants, currentUserEmail) => {
  const emails = new Set();

  restaurants.forEach((restaurant) => {
    const email = (restaurant.store_owner_email || '').trim().toLowerCase();
    if (email) emails.add(email);

    const pendingEmail = (restaurant.pending_store_owner_email || '').trim().toLowerCase();
    if (pendingEmail) emails.add(pendingEmail);
  });

  if (currentUserEmail) {
    emails.add(currentUserEmail.trim().toLowerCase());
  }

  return Array.from(emails).sort();
};

const getAuthRedirectTo = () => `${window.location.origin}/#/`;

const hasPendingOwnerInvite = (restaurant) => (
  restaurant?.pending_store_owner_status === 'awaiting_confirmation' && !!restaurant?.pending_store_owner_email
);

const Dashboard = ({ user, isAdmin, role, canCreateStores = false, showSessionRow = true, showBrandHeader = true, shellBasePath = '/dashboard', defaultStoreSection }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', slug: '', ownerEmail: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [assignRestaurant, setAssignRestaurant] = useState(null); // restaurant to assign owner to
  const [assignEmail, setAssignEmail] = useState('');
  const [assignError, setAssignError] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [editOwnerRestaurant, setEditOwnerRestaurant] = useState(null);
  const [editOwnerFields, setEditOwnerFields] = useState({ name: '', phone: '', email: '' });
  const [editOwnerError, setEditOwnerError] = useState('');
  const [editOwnerSaving, setEditOwnerSaving] = useState(false);
  const [editOwnerDeleting, setEditOwnerDeleting] = useState(false);
  const [pendingOwnerRestaurant, setPendingOwnerRestaurant] = useState(null);
  const [pendingOwnerError, setPendingOwnerError] = useState('');
  const [pendingOwnerResending, setPendingOwnerResending] = useState(false);
  const [pendingOwnerCancelling, setPendingOwnerCancelling] = useState(false);
  const [showStoreRequest, setShowStoreRequest] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('ss_dashboard_view') || 'grid');
  const [search, setSearch] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragNode = useRef(null);
  const navigate = useNavigate();
  const ownerEmailSuggestions = getOwnerEmailSuggestions(restaurants, user?.email);

  const schemaMissing = error?.includes("Could not find the table 'public.restaurants'");

  const setViewMode = (mode) => {
    setView(mode);
    localStorage.setItem('ss_dashboard_view', mode);
  };

  const loadRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        const { data: rows, error } = await supabase
          .from('restaurants')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;
        const savedOrder = JSON.parse(localStorage.getItem(ORDER_KEY(user.id)) || '[]');
        setRestaurants(applyOrder(rows || [], savedOrder));
      } else {
        const { data: rows, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('store_owner_id', user.id)
          .eq('lifecycle_status', 'active');
        if (error) throw error;
        const savedOrder = JSON.parse(localStorage.getItem(ORDER_KEY(user.id)) || '[]');
        setRestaurants(applyOrder(rows || [], savedOrder));
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadRestaurants();
  }, [loadRestaurants, user?.id]);

  const saveOrder = (list) => {
    localStorage.setItem(ORDER_KEY(user.id), JSON.stringify(list.map(r => r.id)));
  };

  const sendOwnerInviteEmail = useCallback(async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: getAuthRedirectTo(),
      },
    });

    if (error) {
      throw error;
    }
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const preparePendingOwner = useCallback(async (email) => {
    const normalizedEmail = email.trim().toLowerCase();
    await sendOwnerInviteEmail(normalizedEmail);

    return {
      pending_store_owner_id: null,
      pending_store_owner_email: normalizedEmail,
      pending_store_owner_status: 'awaiting_confirmation',
      pending_store_owner_invited_at: new Date().toISOString(),
      pending_store_owner_is_new_user: false,
    };
  }, [sendOwnerInviteEmail]);

  const openPendingOwner = (restaurant) => {
    setPendingOwnerRestaurant(restaurant);
    setPendingOwnerError('');
  };

  const resolveStoreRoute = (restaurantId) => {
    const section = defaultStoreSection || (isAdmin ? 'admin' : 'products');
    return `${shellBasePath}/stores/${restaurantId}/${section}`;
  };

  /* ── Drag handlers ── */
  const onDragStart = (e, idx) => {
    dragNode.current = e.currentTarget;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // slight delay so the dragged element renders before ghost is captured
    setTimeout(() => { if (dragNode.current) dragNode.current.classList.add('dragging'); }, 0);
  };

  const onDragEnter = (idx) => {
    if (idx === dragIdx) return;
    setDragOverIdx(idx);
  };

  const onDragEnd = () => {
    if (dragNode.current) dragNode.current.classList.remove('dragging');
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      setRestaurants(prev => {
        const next = [...prev];
        const [moved] = next.splice(dragIdx, 1);
        next.splice(dragOverIdx, 0, moved);
        saveOrder(next);
        return next;
      });
    }
    setDragIdx(null);
    setDragOverIdx(null);
    dragNode.current = null;
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  /* ── CRUD ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) { setFormError('Store name is required.'); return; }
    if (!formData.slug.trim()) { setFormError('Slug is required.'); return; }
    if (!/^[a-z0-9-]+$/.test(formData.slug)) { setFormError('Slug can only contain lowercase letters, numbers, and hyphens.'); return; }
    if (isAdmin) {
      if (!formData.ownerEmail.trim()) { setFormError('Owner email is required.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) { setFormError('Enter a valid email address.'); return; }
    }

    setSaving(true);
    try {
      const insertPayload = {
        owner_id: user.id,
        name: formData.name.trim(),
        slug: formData.slug.trim(),
      };

      if (isAdmin && formData.ownerEmail.trim()) {
        Object.assign(insertPayload, await preparePendingOwner(formData.ownerEmail));
      } else {
        insertPayload.store_owner_id = user.id;
        insertPayload.store_owner_email = user.email;
      }

      const { error } = await supabase.from('restaurants').insert(insertPayload);
      if (error) throw error;
      setShowForm(false);
      setFormData({ name: '', description: '', slug: '', ownerEmail: '' });
      await loadRestaurants();
    } catch (err) {
      setFormError(err.message || 'Failed to create store.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (restaurant) => {
    if (!window.confirm(`Delete "${restaurant.name}"? This cannot be undone.`)) return;
    setDeletingId(restaurant.id);
    try {
      const { error } = await supabase.from('restaurants').delete().eq('id', restaurant.id);
      if (error) throw error;
      await loadRestaurants();
    } catch (err) {
      alert('Failed to delete: ' + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setFormData(f => ({ ...f, name, slug }));
  };

  const openEditOwner = async (restaurant) => {
    setEditOwnerRestaurant(restaurant);
    setEditOwnerError('');
    setEditOwnerFields({ name: '', phone: '', email: restaurant.store_owner_email || '' });
  };

  const handleEditOwnerSave = async (e) => {
    e.preventDefault();
    setEditOwnerError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editOwnerFields.email)) {
      setEditOwnerError('Enter a valid email address.');
      return;
    }
    setEditOwnerSaving(true);
    try {
      const normalizedEmail = editOwnerFields.email.trim().toLowerCase();

      if (normalizedEmail === (editOwnerRestaurant.store_owner_email || '').trim().toLowerCase()) {
        setEditOwnerRestaurant(null);
        return;
      }

      const pendingOwnerPayload = await preparePendingOwner(normalizedEmail);
      await adminSetPendingStoreOwner(
        editOwnerRestaurant.id,
        pendingOwnerPayload.pending_store_owner_email,
        pendingOwnerPayload.pending_store_owner_invited_at,
      );

      setEditOwnerRestaurant(null);
      window.alert('Replacement invitation sent. The store will remain unassigned until the new owner confirms.');
      await loadRestaurants();
    } catch (err) {
      setEditOwnerError(err.message || 'Failed to save changes.');
    } finally {
      setEditOwnerSaving(false);
    }
  };

  const handleDeleteOwner = async () => {
    if (!window.confirm(`Remove owner ${editOwnerRestaurant.store_owner_email} from this store?`)) return;
    setEditOwnerDeleting(true);
    try {
      await adminClearStoreOwner(editOwnerRestaurant.id);
      setEditOwnerRestaurant(null);
      await loadRestaurants();
    } catch (err) {
      setEditOwnerError(err.message || 'Failed to delete account.');
    } finally {
      setEditOwnerDeleting(false);
    }
  };

  const handleAssignOwner = async (e) => {
    e.preventDefault();
    setAssignError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assignEmail)) { setAssignError('Enter a valid email address.'); return; }
    setAssigning(true);
    try {
      const pendingOwnerPayload = await preparePendingOwner(assignEmail);
      await adminSetPendingStoreOwner(
        assignRestaurant.id,
        pendingOwnerPayload.pending_store_owner_email,
        pendingOwnerPayload.pending_store_owner_invited_at,
      );

      setAssignRestaurant(null);
      setAssignEmail('');
      window.alert('Invitation sent. Awaiting confirmation.');
      await loadRestaurants();
    } catch (err) {
      setAssignError(err.message || 'Failed to assign owner.');
    } finally {
      setAssigning(false);
    }
  };

  const handleResendPendingOwner = async () => {
    if (!pendingOwnerRestaurant?.pending_store_owner_email) return;
    setPendingOwnerError('');
    setPendingOwnerResending(true);
    try {
      const pendingOwnerPayload = await preparePendingOwner(pendingOwnerRestaurant.pending_store_owner_email);
      await adminSetPendingStoreOwner(
        pendingOwnerRestaurant.id,
        pendingOwnerPayload.pending_store_owner_email,
        pendingOwnerPayload.pending_store_owner_invited_at,
      );

      setPendingOwnerRestaurant((current) => (current ? { ...current, ...pendingOwnerPayload } : current));
      await loadRestaurants();
    } catch (err) {
      setPendingOwnerError(err.message || 'Failed to resend invitation.');
    } finally {
      setPendingOwnerResending(false);
    }
  };

  const handleCancelPendingOwner = async () => {
    if (!pendingOwnerRestaurant) return;
    setPendingOwnerError('');
    setPendingOwnerCancelling(true);
    try {
      await adminClearStoreOwner(pendingOwnerRestaurant.id);
      setPendingOwnerRestaurant(null);
      await loadRestaurants();
    } catch (err) {
      setPendingOwnerError(err.message || 'Failed to cancel invitation.');
    } finally {
      setPendingOwnerCancelling(false);
    }
  };

  /* ── Filter ── */
  const q = search.trim().toLowerCase();
  const filtered = q
    ? restaurants.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        getShortId(r.id).includes(q) ||
        (getStoreIndustry(r) || '').toLowerCase().includes(q)
      )
    : restaurants;

  const isSearching = q.length > 0;

  if (loading) return <div className="dashboard-loading">Loading stores...</div>;
  if (schemaMissing) {
    return (
      <main className="dashboard-page">
        <div className="dashboard-setup-card">
          <h1>Database Setup Needed</h1>
          <p>
            Sign-in worked. The app is now trying to load your stores,
            but the <strong>restaurants</strong> table does not exist yet in the database.
          </p>
          <ol className="dashboard-setup-steps">
            <li>Open Supabase.</li>
            <li>Go to SQL Editor.</li>
            <li>Run the schema in <code>servsolo-multi/supabase/schema.sql</code>.</li>
            <li>Refresh this page.</li>
          </ol>
          <div className="dashboard-setup-note">
            Your auth user ID is visible in Supabase under Authentication, Users.
          </div>
        </div>
      </main>
    );
  }
  if (error) return <div className="dashboard-error">Error: {error}</div>;

  return (
    <main className="dashboard-page">
      <div className="dashboard-header">
        {showBrandHeader && (
          <div className="dashboard-brand-row">
            <h1 className="dashboard-title">Serv<span className="dashboard-title-accent">Solo</span> Stores</h1>
            <p className="dashboard-subtitle">Manage your store settings, products and modifiers.</p>
            {showSessionRow && (
              <div className="dashboard-session-row">
                <div className="dashboard-session-chip">
                  <span className={`dashboard-role-badge${isAdmin ? ' admin' : ''}`}>{isAdmin ? 'Admin' : (role || 'user')}</span>
                  <span className="dashboard-session-email">{user?.email || 'Unknown user'}</span>
                </div>
                <button className="dashboard-signout-btn" onClick={handleSignOut}>Sign Out</button>
              </div>
            )}
          </div>
        )}
        {!showBrandHeader && showSessionRow && (
          <div className="dashboard-brand-row dashboard-brand-row-compact">
            <div className="dashboard-session-row">
              <div className="dashboard-session-chip">
                <span className={`dashboard-role-badge${isAdmin ? ' admin' : ''}`}>{isAdmin ? 'Admin' : (role || 'user')}</span>
                <span className="dashboard-session-email">{user?.email || 'Unknown user'}</span>
              </div>
              <button className="dashboard-signout-btn" onClick={handleSignOut}>Sign Out</button>
            </div>
          </div>
        )}

        <div className={`dashboard-toolbar${showBrandHeader ? '' : ' dashboard-toolbar-shell'}`}>
          <div className="search-wrap">
            <svg className="search-icon" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="6.5" cy="6.5" r="5"/><line x1="10.5" y1="10.5" x2="14" y2="14"/>
            </svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search by store name or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} title="Clear">✕</button>
            )}
          </div>
          {isSearching && (
            <span className="search-count">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          )}
          <div className="view-toggle">
            <button className={`view-btn${view === 'grid' ? ' active' : ''}`} onClick={() => setViewMode('grid')} title="Grid view">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
                <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
              </svg>
            </button>
            <button className={`view-btn${view === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')} title="List view">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="2" width="14" height="2.5" rx="1"/><rect x="1" y="6.75" width="14" height="2.5" rx="1"/>
                <rect x="1" y="11.5" width="14" height="2.5" rx="1"/>
              </svg>
            </button>
          </div>
          {(isAdmin || canCreateStores) && <button className="btn-create" onClick={() => setShowForm(true)}>+ New Store</button>}
          {!isAdmin && <button className="btn-create btn-create-secondary" onClick={() => setShowStoreRequest(true)}>+ Request Store</button>}
        </div>
      </div>

      {filtered.length === 0 && !isSearching ? (
        <div className="dashboard-empty">
          <div className="dashboard-empty-icon">🏪</div>
          <div className="dashboard-empty-text">No stores yet</div>
          <div className="dashboard-empty-sub">{isAdmin || canCreateStores ? <>Click <strong>+ New Store</strong> to get started.</> : 'Store creation is managed by Servsolo admin.'}</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="dashboard-empty">
          <div className="dashboard-empty-icon">🔍</div>
          <div className="dashboard-empty-text">No results for "{search}"</div>
          <div className="dashboard-empty-sub">Try a different name or ID.</div>
        </div>
      ) : view === 'grid' ? (
        <div className="store-grid">
          {filtered.map((restaurant, idx) => {
            const logo = getStoreLogo(restaurant);
            const industry = getStoreIndustry(restaurant);
            const [c1, c2] = getAvatarColor(restaurant.id);
            const isDragOver = dragOverIdx === idx && dragIdx !== idx;
            return (
              <div
                key={restaurant.id}
                className={`store-card${isDragOver ? ' drag-over' : ''}`}
                draggable={!isSearching}
                onDragStart={isSearching ? undefined : (e) => onDragStart(e, idx)}
                onDragEnter={isSearching ? undefined : () => onDragEnter(idx)}
                onDragOver={isSearching ? undefined : onDragOver}
                onDragEnd={isSearching ? undefined : onDragEnd}
                onClick={() => navigate(resolveStoreRoute(restaurant.id))}
              >
                <div
                  className="store-card-cover"
                  style={logo ? undefined : { background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                >
                  {logo
                    ? <img src={logo} alt={restaurant.name} className="store-cover-img" />
                    : <div className="store-cover-initials">{getInitials(restaurant.name)}</div>
                  }
                  {!isSearching && <div className="drag-handle" title="Drag to reorder">⠿</div>}
                  <button
                    className="btn-delete-store card-delete"
                    title="Delete store"
                    disabled={deletingId === restaurant.id}
                    onClick={(e) => { e.stopPropagation(); handleDelete(restaurant); }}
                  >
                    {deletingId === restaurant.id ? '…' : '✕'}
                  </button>
                </div>
                <div className="store-card-body">
                  <div className="store-card-name">{restaurant.name || 'Untitled Store'}</div>
                  <div className="store-card-id">Store ID: {getShortId(restaurant.id)}</div>
                  <div className="store-card-desc">{industry || 'No industry set'}</div>
                  {restaurant.slug && (
                    <div className="store-card-slug">{getStoreHost(restaurant.slug)}</div>
                  )}
                  <button
                    className="store-card-website-btn"
                    title="Edit website"
                    onClick={(e) => { e.stopPropagation(); navigate(`${shellBasePath}/stores/${restaurant.id}/website`); }}
                  >
                    Website
                  </button>
                  {hasPendingOwnerInvite(restaurant) && isAdmin ? (
                    <>
                      <div className="store-card-pending-owner store-card-owner-link" onClick={e => { e.stopPropagation(); openPendingOwner(restaurant); }}>Awaiting confirmation</div>
                      <div className="store-card-owner-email">{restaurant.pending_store_owner_email}</div>
                    </>
                  ) : !restaurant.store_owner_id && isAdmin ? (
                    <div className="store-card-no-owner" onClick={e => { e.stopPropagation(); setAssignRestaurant(restaurant); setAssignEmail(''); setAssignError(''); }}>⚠ Assign Owner!</div>
                  ) : restaurant.store_owner_email && isAdmin ? (
                    <div className="store-card-owner-email store-card-owner-link" onClick={e => { e.stopPropagation(); openEditOwner(restaurant); }}>{restaurant.store_owner_email}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="store-list">
          {filtered.map((restaurant, idx) => {
            const logo = getStoreLogo(restaurant);
            const industry = getStoreIndustry(restaurant);
            const [c1, c2] = getAvatarColor(restaurant.id);
            const isDragOver = dragOverIdx === idx && dragIdx !== idx;
            return (
              <div
                key={restaurant.id}
                className={`store-list-row${isDragOver ? ' drag-over' : ''}`}
                draggable={!isSearching}
                onDragStart={isSearching ? undefined : (e) => onDragStart(e, idx)}
                onDragEnter={isSearching ? undefined : () => onDragEnter(idx)}
                onDragOver={isSearching ? undefined : onDragOver}
                onDragEnd={isSearching ? undefined : onDragEnd}
                onClick={() => navigate(resolveStoreRoute(restaurant.id))}
              >
                {!isSearching && <div className="drag-handle list-drag-handle" title="Drag to reorder">⠿</div>}
                <div className="store-list-logo" style={logo ? undefined : { background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                  {logo
                    ? <img src={logo} alt={restaurant.name} className="store-logo-img" />
                    : <div className="store-logo-initials">{getInitials(restaurant.name)}</div>
                  }
                </div>
                <div className="store-list-info">
                  <div className="store-card-name">{restaurant.name || 'Untitled Store'}</div>
                  <div className="store-card-id">Store ID: {getShortId(restaurant.id)}</div>
                  <div className="store-card-desc">{industry || 'No industry set'}</div>
                  {restaurant.slug && (
                    <div className="store-card-slug">{getStoreHost(restaurant.slug)}</div>
                  )}
                  {hasPendingOwnerInvite(restaurant) && isAdmin
                    ? <>
                        <div className="store-card-pending-owner store-card-owner-link" onClick={e => { e.stopPropagation(); openPendingOwner(restaurant); }}>Awaiting confirmation</div>
                        <div className="store-card-owner-email">{restaurant.pending_store_owner_email}</div>
                      </>
                    : !restaurant.store_owner_id && isAdmin
                    ? <div className="store-card-no-owner" onClick={e => { e.stopPropagation(); setAssignRestaurant(restaurant); setAssignEmail(''); setAssignError(''); }}>⚠ Assign Owner!</div>
                    : restaurant.store_owner_email && isAdmin
                    ? <div className="store-card-owner-email store-card-owner-link" onClick={e => { e.stopPropagation(); openEditOwner(restaurant); }}>{restaurant.store_owner_email}</div>
                    : null
                  }
                </div>
                <button
                  className="store-card-website-btn store-list-website-btn"
                  title="Edit website"
                  onClick={(e) => { e.stopPropagation(); navigate(`${shellBasePath}/stores/${restaurant.id}/website`); }}
                >
                  Website
                </button>
                <button
                  className="btn-delete-store"
                  title="Delete store"
                  disabled={deletingId === restaurant.id}
                  onClick={(e) => { e.stopPropagation(); handleDelete(restaurant); }}
                >
                  {deletingId === restaurant.id ? '…' : '✕'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editOwnerRestaurant && (
        <div className="modal-overlay" onClick={() => setEditOwnerRestaurant(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h2>Edit Owner — {editOwnerRestaurant.name}</h2>
            <form onSubmit={handleEditOwnerSave}>
              <div className="owner-invite-status-card owner-invite-status-card-neutral">
                <div className="owner-invite-status-row">
                  <span className="owner-invite-status-badge owner-invite-status-badge-blue">Assigned</span>
                </div>
                <div className="owner-invite-note">To replace this owner, enter a new email below. A confirmation email will be sent and the store will remain unassigned until that email signs in.</div>
              </div>
              <div className="form-group">
                <label>Owner Email *</label>
                <input
                  type="email"
                  placeholder="owner@store.com"
                  value={editOwnerFields.email}
                  onChange={e => setEditOwnerFields(f => ({ ...f, email: e.target.value }))}
                  autoFocus
                />
                <span className="form-hint">Save changes to send a replacement invitation, or delete owner to remove access immediately.</span>
              </div>
              {editOwnerError && <div className="form-error">{editOwnerError}</div>}
              <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                <button
                  type="button"
                  className="btn-delete-store"
                  style={{ padding: '8px 16px', borderRadius: 8 }}
                  disabled={editOwnerDeleting}
                  onClick={handleDeleteOwner}
                >
                  {editOwnerDeleting ? 'Removing...' : 'Delete Owner'}
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-cancel" onClick={() => setEditOwnerRestaurant(null)}>Cancel</button>
                  <button type="submit" className="btn-save" disabled={editOwnerSaving}>{editOwnerSaving ? 'Sending...' : 'Save Changes'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingOwnerRestaurant && (
        <div className="modal-overlay" onClick={() => setPendingOwnerRestaurant(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h2>Owner Invitation - {pendingOwnerRestaurant.name}</h2>
            <div className="owner-invite-status-card">
              <div className="owner-invite-status-row">
                <span className="owner-invite-status-badge">Awaiting Confirmation</span>
                {pendingOwnerRestaurant.pending_store_owner_invited_at && (
                  <span className="owner-invite-status-time">Sent {new Date(pendingOwnerRestaurant.pending_store_owner_invited_at).toLocaleString()}</span>
                )}
              </div>
              <div className="owner-invite-email">{pendingOwnerRestaurant.pending_store_owner_email}</div>
              <div className="owner-invite-note">This store will remain unassigned until that email signs in and confirms the invitation link.</div>
            </div>
            {pendingOwnerError && <div className="form-error">{pendingOwnerError}</div>}
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <button
                type="button"
                className="btn-delete-store"
                style={{ padding: '8px 16px', borderRadius: 8 }}
                disabled={pendingOwnerCancelling}
                onClick={handleCancelPendingOwner}
              >
                {pendingOwnerCancelling ? 'Cancelling...' : 'Cancel Invitation'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-cancel" onClick={() => setPendingOwnerRestaurant(null)}>Close</button>
                <button type="button" className="btn-save" disabled={pendingOwnerResending} onClick={handleResendPendingOwner}>
                  {pendingOwnerResending ? 'Sending...' : 'Resend Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assignRestaurant && (
        <div className="modal-overlay" onClick={() => setAssignRestaurant(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h2>Assign Owner — {assignRestaurant.name}</h2>
            <form onSubmit={handleAssignOwner}>
              <div className="form-group">
                <label>Owner Email *</label>
                <input
                  type="email"
                  placeholder="owner@theirstore.com"
                  value={assignEmail}
                  onChange={e => setAssignEmail(e.target.value)}
                  list="owner-email-suggestions"
                  autoFocus
                />
                <span className="form-hint">An invitation email will be sent, and the store will stay in awaiting confirmation until they use it.</span>
              </div>
              {assignError && <div className="form-error">{assignError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setAssignRestaurant(null)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={assigning}>{assigning ? 'Sending...' : 'Send Invitation'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h2>New Store</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Store Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Kebab Prime"
                  value={formData.name}
                  onChange={handleNameChange}
                  autoFocus
                />
              </div>
              {isAdmin && (
                <div className="form-group">
                  <label>Owner Email *</label>
                  <input
                    type="email"
                    placeholder="owner@theirstore.com"
                    value={formData.ownerEmail}
                    onChange={e => setFormData(f => ({ ...f, ownerEmail: e.target.value }))}
                    list="owner-email-suggestions"
                  />
                    <span className="form-hint">They'll receive an invitation email, and the store will remain pending until they confirm it.</span>
                </div>
              )}
              <div className="form-group">
                <label>URL Slug *</label>
                <input
                  type="text"
                  placeholder="kebab-prime"
                  value={formData.slug}
                  onChange={e => setFormData(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                />
                <span className="form-hint">Online: <strong>{getStoreHost(formData.slug || 'your-slug')}</strong>  Kiosk: <strong>{getStoreKioskUrl(formData.slug || 'your-slug').replace(/^https?:\/\//, '')}</strong></span>
              </div>
              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Creating...' : 'Create Store'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <datalist id="owner-email-suggestions">
        {ownerEmailSuggestions.map((email) => (
          <option key={email} value={email} />
        ))}
      </datalist>

      {showStoreRequest && (
        <div className="modal-overlay" onClick={() => setShowStoreRequest(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <StoreRequestForm
              user={user}
              compact
              title="Request Another Store"
              subtitle="We'll review your request and add the new store to your account once approved."
              onSuccess={() => setShowStoreRequest(false)}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default Dashboard;
